// Upload search — drop any image, find the closest artworks. The image is
// downscaled to ≤384px on a canvas, sent as base64, embedded by the Lambda
// in memory and never stored anywhere.
import { useState } from "preact/hooks";
import { api } from "../../api";
import { Grid, type Item } from "./Grid";

interface UploadSearchProps {
  gated: boolean;
  onSeed: (item: Item) => void;
}

async function fileToB64(file: File): Promise<{ b64: string; preview: string }> {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.src = url;
  await img.decode();
  const scale = Math.min(1, 384 / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
  URL.revokeObjectURL(url);
  const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
  return { b64: dataUrl.split(",")[1]!, preview: dataUrl };
}

export function UploadSearch({ gated, onSeed }: UploadSearchProps) {
  const [items, setItems] = useState<Item[]>([]);
  const [preview, setPreview] = useState("");
  const [status, setStatus] = useState("drop an image or pick a file");
  const [busy, setBusy] = useState(false);

  async function run(file: File) {
    if (!file.type.startsWith("image/")) {
      setStatus("⚠ not an image file");
      return;
    }
    setBusy(true);
    setStatus("embedding image… (cold start can take ~1 min)");
    try {
      const { b64, preview: p } = await fileToB64(file);
      setPreview(p);
      const res = await api.search.vector({ imageB64: b64, gated, k: 48 });
      setItems(res.items);
      setStatus(`${res.items.length} closest artworks (${res.tookMs} ms) — image was not stored`);
    } catch (e) {
      setStatus(`⚠ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p class="ls-meta">
        Find artworks that match any picture — an interior photo, a mood board, a sketch. The image
        is embedded in memory and never stored.
      </p>
      <div class="ls-row">
        <label
          class="ls-drop"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer?.files?.[0];
            if (f) void run(f);
          }}
        >
          {preview ? (
            <img src={preview} alt="query" />
          ) : (
            <span>drop image here / click to browse</span>
          )}
          <input
            type="file"
            accept="image/*"
            style="display:none"
            disabled={busy}
            onChange={(e) => {
              const f = (e.target as HTMLInputElement).files?.[0];
              if (f) void run(f);
            }}
          />
        </label>
        <p class="ls-meta" style="flex:1">
          {status}
        </p>
      </div>
      <Grid items={items} onCell={(it) => onSeed(it)} />
    </div>
  );
}
